import React from 'react'
import { QueryClient, QueryClientProvider } from 'react-query'
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom'
import { RequireAdmin, RequireAuth, UserAuthProvider } from './hooks/context/UserAuthContext'
import { AccountsProvider } from './hooks/context/AccountsContext'
import { SnackbarProvider } from 'notistack'
import moment from 'moment'
import { createTheme } from '@mui/material/styles'
import red from '@mui/material/colors/red'
import RedirectProxy from './utils/redirectProxy'
import Admin from './pages/Admin'
import CreateUser from './pages/CreateUser'
import EditUser from './pages/EditUser'
import Protect from './components/_ui/Protect'
import { MapAdminScreen } from './components/map/Page'
import { CreateMap } from './components/map/Create'
import { EditMap } from './components/map/Edit'
import { TransactionScreen } from './components/map/transaction/Page'
import { MapDashboardScreen } from './components/map/dashboard/Page'

const ThemeProvider = React.lazy(() => import('@mui/material/styles/ThemeProvider'))
const CssBaseline = React.lazy(() => import('@mui/material/CssBaseline'))

const Layout = React.lazy(() => import('./components/_ui/Layout'))
const NotFound = React.lazy(() => import('./pages/NotFound'))

// const Login = React.lazy(() => import('./pages/Login'))
// const ForgotPassword = React.lazy(() => import('./pages/ForgotPassword'))
// const ResetPassword = React.lazy(() => import('./pages/ResetPassword'))
const Home = React.lazy(() => import('./pages/Home'))
const AgingSummary = React.lazy(() => import('./pages/documents/AgingSummary'))
const MapSummary = React.lazy(() => import('./pages/documents/MapSummary'))
const ViewPDF = React.lazy(() => import('./pages/ViewPDF'))
const AccountSelect = React.lazy(() => import('./pages/documents/AccountSelect'))
const LinkedCompanies = React.lazy(() => import('./pages/documents/LinkedCompanies'))
const DocumentSelect = React.lazy(() => import('./pages/documents/DocumentSelect'))
const Documents = React.lazy(() => import('./pages/documents/Documents'))

moment.locale('en-sg')

const theme = createTheme({
  overrides: {
    MuiCssBaseline: {
      '@global': {
        a: {
          color: 'inherit',
          textDecoration: 'none',
        },
      },
    },
    MuiContainer: {
      root: {
        paddingBottom: 24,
      },
    },
    MuiTableCell: {
      root: {
        overflow: 'hidden',
      },
    },
  },
  palette: {
    primary: {
      main: '#000',
    },
    secondary: {
      main: red[500],
    },
  },
})

const queryClient = new QueryClient()

function App() {
  return (
    <React.Suspense fallback={<div />}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Router>
          <QueryClientProvider client={queryClient}>
            <SnackbarProvider>
              <UserAuthProvider>
                <Protect>
                  <AccountsProvider>
                    <Routes>
                      <Route element={<Layout />}>
                        <Route path='/profile' element={<RedirectProxy />} />

                        {/* Protected routes */}
                        <Route element={<RequireAuth />}>
                          <Route path='/' element={<Home />} />
                          <Route index path='/customer-aging-summary' element={<AgingSummary />} />
                          <Route index path='/customer-map-summary' element={<MapSummary />} />
                          <Route path='/admin' element={<RequireAdmin />}>
                            <Route index element={<Admin />} />
                            <Route path='users/create' element={<CreateUser />} />
                            <Route path='users/edit/:userId' element={<EditUser />} />
                            <Route path='*' element={<NotFound />} />
                          </Route>
                          <Route path='/map' element={<RequireAdmin />}>
                            <Route index element={<MapAdminScreen />} />
                            <Route path='summary' element={<MapDashboardScreen />} />
                            <Route path='create' element={<CreateMap />} />
                            <Route path=':id' element={<EditMap />} />
                            <Route path='transaction/:id' element={<TransactionScreen />} />
                            <Route path='*' element={<NotFound />} />
                          </Route>
                          <Route path='*' element={<NotFound />} />
                          <Route
                            path='/:htgCompany/:companyAccountId/:documentType/:yearMonth'
                            element={<ViewPDF />}
                          />
                          <Route
                            path='/:htgCompany/:companyAccountId/:documentType/:yearMonth/:docId'
                            element={<ViewPDF />}
                          />
                          <Route path='/linked-companies' element={<LinkedCompanies />} />
                          <Route path='/:htgCompany' element={<AccountSelect />}>
                            <Route path=':companyAccountId' element={<DocumentSelect />}>
                              <Route path=':documentType' element={<Documents />} />
                            </Route>
                          </Route>
                        </Route>
                      </Route>
                    </Routes>
                  </AccountsProvider>
                </Protect>
              </UserAuthProvider>
            </SnackbarProvider>
          </QueryClientProvider>
        </Router>
      </ThemeProvider>
    </React.Suspense>
  )
}

export default App
